import {Button, CircularProgress, Grid, Paper, TextField, Typography} from '@material-ui/core';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {isLoaded, Loaded} from 'Core/models/Async';
import {EmptyString, NonEmptyString} from 'Core/models/EmptyString';
import {InvalidPhoneNumber, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import ValidatableEmailInput from 'Shared/components/ValidatableEmailInput';
import ValidatableTextInput from 'Shared/components/ValidatableTextInput';
import {maskPhoneNumber, unmaskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';
import {
  addBeneficiariesUser,
  addBOSToolsUser,
  changeUser,
  DEFAULT_STATE,
  updateField,
  validateEmail,
  validateUsername,
} from 'Users/ducks/bosAndBeneficiariesUserCreation';
import {
  BOSAndBeneficiariesUser,
  InvalidBOSAndBeneficiariesUser,
  UserType,
  ValidBOSAndBeneficiariesUser,
} from 'Users/models/BOSAndBeneficiariesUser';
import './BOSAndBeneficiariesUserCreation.sass';

type BOSAndBeneficiariesUserCreationProps = {
  userType: UserType;
};

const BOSAndBeneficiariesUserCreation = ({userType}: BOSAndBeneficiariesUserCreationProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {bosAndBeneficiariesUser} = useSelector((state: ReducerState) => state.user.bosAndBeneficiariesUser);

  const fieldIsRequiredErrorMessage = 'Field is Required';

  useEffect(() => {
    if (!userType) {
      history.push(CoreRouteConstants.DASHBOARD);
    }

    return () => {
      dispatch(changeUser(DEFAULT_STATE.bosAndBeneficiariesUser as Loaded<BOSAndBeneficiariesUser>));
    };
  }, [userType]);

  return (
    <Grid className="bosandbeneficiaries_user_creation" container alignItems="center" direction="column">
      <Paper className="bosandbeneficiaries_user_creation__paper">
        <Typography variant="h6" className="bosandbeneficiaries_user_creation__title">
          {`Add ${userType === UserType.BOSToolsUser ? 'BOS Tools' : 'Beneficiaries'} User`}
        </Typography>
        {isLoaded(bosAndBeneficiariesUser) ? (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ValidatableTextInput
                value={bosAndBeneficiariesUser.Data.username!}
                onValidate={bosAndBeneficiariesUsername => dispatch(validateUsername(bosAndBeneficiariesUsername))}
                label="Username"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="First Name"
                value={bosAndBeneficiariesUser.Data.firstName?.value || ''}
                onChange={e => dispatch(updateField({firstName: NonEmptyString.create(e.target.value)}))}
                required
                error={bosAndBeneficiariesUser.Data.firstName instanceof EmptyString}
                helperText={
                  bosAndBeneficiariesUser.Data.firstName instanceof EmptyString && fieldIsRequiredErrorMessage
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Last Name"
                value={bosAndBeneficiariesUser.Data.lastName?.value || ''}
                onChange={e => dispatch(updateField({lastName: NonEmptyString.create(e.target.value)}))}
                required
                error={bosAndBeneficiariesUser.Data.lastName instanceof EmptyString}
                helperText={bosAndBeneficiariesUser.Data.lastName instanceof EmptyString && fieldIsRequiredErrorMessage}
              />
            </Grid>
            <Grid item xs={12}>
              <ValidatableEmailInput
                value={bosAndBeneficiariesUser.Data.email!}
                onValidate={email => dispatch(validateEmail(email))}
                label="Email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="(XXX)-XXX XXXX"
                inputProps={{
                  maxLength: 14,
                }}
                variant="outlined"
                label="Phone Number"
                value={maskPhoneNumber(bosAndBeneficiariesUser.Data.phoneNumber?.phoneNumber) || ''}
                onChange={e =>
                  dispatch(updateField({phoneNumber: ValidPhoneNumber.create(unmaskPhoneNumber(e.target.value))}))
                }
                error={bosAndBeneficiariesUser.Data.phoneNumber instanceof InvalidPhoneNumber}
                helperText={
                  bosAndBeneficiariesUser.Data.phoneNumber instanceof InvalidPhoneNumber &&
                  bosAndBeneficiariesUser.Data.phoneNumber.validationError
                }
              />
            </Grid>
            <Grid item container xs={12} justify="flex-end">
              <Button
                color="primary"
                variant="contained"
                disabled={
                  ValidBOSAndBeneficiariesUser.create(bosAndBeneficiariesUser.Data) instanceof
                  InvalidBOSAndBeneficiariesUser
                }
                onClick={() =>
                  userType === UserType.BeneficiariesUser
                    ? dispatch(addBeneficiariesUser(bosAndBeneficiariesUser.Data))
                    : dispatch(addBOSToolsUser(bosAndBeneficiariesUser.Data))
                }
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        ) : (
          <div className="bosandbeneficiaries_user_creation__loading-container">
            <CircularProgress color="primary" />
          </div>
        )}
      </Paper>
    </Grid>
  );
};

export default BOSAndBeneficiariesUserCreation;
